export enum AlertStatus {
    Open = 'Open',
    Acknowledged = 'Acknowledged',
    Resolved = 'Resolved',
    Escalated = 'Escalated',
}

export enum AlertSeverity {
    Low = 'Low',
    Moderate = 'Moderate',
    High = 'High',
}

export interface IClinicalAlert {
    id: number;
    alertRuleID: number;
    alertRuleConfigurationID: number | null;
    patientID: number;
    patientName: string | null;
    healthcareFacilityID: number;
    assignedHcpID: number | null;
    status: AlertStatus;
    severity: AlertSeverity;
    titleEn: string;
    titleFr: string;
    descriptionEn: string | null;
    descriptionFr: string | null;
    sourceType: string;
    sourceEntityID: number | null;
    triggerValue: string | null;
    triggerDataJson: string | null;
    triggeredDate: string;
    acknowledgedDate: string | null;
    acknowledgedByUserID: number | null;
    resolvedDate: string | null;
    resolvedByUserID: number | null;
    resolutionNotes: string | null;
    escalatedDate: string | null;
    escalationLevel: number;
    patientNotified: boolean;
    patientNotifiedDate: string | null;
    isVisited: boolean;
}

export interface IClinicalAlertComment {
    id: number;
    clinicalAlertID: number;
    userID: number;
    userName: string | null;
    comment: string;
    commentDate: string;
}

export interface IAlertSummary {
    openCount: number;
    acknowledgedCount: number;
    escalatedCount: number;
    resolvedCount: number;
}

export interface IAlertAuditLogItem {
    id: number;
    action: string;
    actorUserID: number | null;
    actorName: string | null;
    actorType: string;
    details: Record<string, unknown> | null;
    eventDate: string;
}

export interface IPatientNotification {
    id: number;
    clinicalAlertID: number;
    sentByUserID: number;
    sentByUserName: string | null;
    message: string;
    sentDate: string;
    deliveryChannel: string;
}

export interface IAssistantNotification {
    id: number;
    clinicalAlertID: number;
    sentByUserID: number;
    sentByUserName: string | null;
    message: string;
    sentDate: string;
    deliveryChannel: string;
}

export interface IAlertDetail extends IClinicalAlert {
    patientDateOfBirth: string | null;
    primaryHcpName: string | null;
    assistantName: string | null;
    assistantEmail: string | null;
    comments: IClinicalAlertComment[];
    auditTrail: IAlertAuditLogItem[];
    patientNotifications: IPatientNotification[];
    assistantNotifications: IAssistantNotification[];
}

export interface IAlertRule {
    id: number;
    code: string;
    nameEn: string;
    nameFr: string;
    descriptionEn: string;
    descriptionFr: string;
    sourceType: string;
    evaluationMode: string;
    isSystemRule: boolean;
    isEnabled: boolean;
}

export interface IAlertRuleConfiguration {
    id: number;
    alertRuleID: number;
    healthcareFacilityID: number | null;
    healthcareProfessionalID: number | null;
    sourceEntityID: number | null;
    conditionJson: string;
    severity: string;
    deliveryChannels: string;
    escalationEnabled: boolean;
    escalationDelayMinutes: number | null;
    escalationTargetType: string | null;
    escalationTargetID: number | null;
    escalationEmailRecipients: string | null;
    escalationSmsRecipients: string | null;
    level2EscalationEnabled: boolean;
    level2EscalationDelayMinutes: number | null;
    level2EscalationTargetType: string | null;
    level2EscalationTargetID: number | null;
    level2EscalationEmailRecipients: string | null;
    level2EscalationSmsRecipients: string | null;
    isEnabled: boolean;
    notificationEnabled: boolean;
    notificationHcpID: number | null;
    notifyByEmail: boolean;
    notifyBySMS: boolean;
    escalationNotifyByEmail: boolean;
    escalationNotifyBySMS: boolean;
    level2EscalationNotifyByEmail: boolean;
    level2EscalationNotifyBySMS: boolean;
    notifyPatientBySMS: boolean;
}

export interface ICreateAlertRuleConfigurationRequest {
    healthcareFacilityID?: number | null;
    healthcareProfessionalID?: number | null;
    sourceEntityID?: number | null;
    conditionJson: string;
    severity: string;
    deliveryChannels: string;
    escalationEnabled: boolean;
    escalationDelayMinutes?: number | null;
    escalationTargetType?: string | null;
    escalationTargetID?: number | null;
    escalationEmailRecipients?: string | null;
    escalationSmsRecipients?: string | null;
    level2EscalationEnabled: boolean;
    level2EscalationDelayMinutes?: number | null;
    level2EscalationTargetType?: string | null;
    level2EscalationTargetID?: number | null;
    level2EscalationEmailRecipients?: string | null;
    level2EscalationSmsRecipients?: string | null;
    isEnabled: boolean;
    notificationEnabled: boolean;
    notificationHcpID?: number | null;
    notifyByEmail: boolean;
    notifyBySMS: boolean;
    escalationNotifyByEmail: boolean;
    escalationNotifyBySMS: boolean;
    level2EscalationNotifyByEmail: boolean;
    level2EscalationNotifyBySMS: boolean;
    notifyPatientBySMS: boolean;
}

export interface IUpdateAlertRuleConfigurationRequest {
    sourceEntityID?: number | null;
    conditionJson: string;
    severity: string;
    deliveryChannels: string;
    escalationEnabled: boolean;
    escalationDelayMinutes?: number | null;
    escalationTargetType?: string | null;
    escalationTargetID?: number | null;
    escalationEmailRecipients?: string | null;
    escalationSmsRecipients?: string | null;
    level2EscalationEnabled: boolean;
    level2EscalationDelayMinutes?: number | null;
    level2EscalationTargetType?: string | null;
    level2EscalationTargetID?: number | null;
    level2EscalationEmailRecipients?: string | null;
    level2EscalationSmsRecipients?: string | null;
    healthcareFacilityID?: number | null;
    isEnabled: boolean;
    notificationEnabled: boolean;
    notificationHcpID?: number | null;
    notifyByEmail: boolean;
    notifyBySMS: boolean;
    escalationNotifyByEmail: boolean;
    escalationNotifyBySMS: boolean;
    level2EscalationNotifyByEmail: boolean;
    level2EscalationNotifyBySMS: boolean;
    notifyPatientBySMS: boolean;
}
